import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ear, Waveform, Footprints, Person, UsersThree, Confetti, EnvelopeSimple, CalendarBlank, MapPin, Backpack } from "@phosphor-icons/react";
import { rcApi, RC, errMsg } from "./rcApi";
import { RcCard, RcTitle, RcButton, RulesBox, RcFooter } from "./rcUi";
import { meetingLabel } from "./Rencontres";

const STEP_ICONS = [Ear, Waveform, Footprints, Person, UsersThree, Confetti];

export default function RythmesConnexionsPublic() {
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "Rythmes & Connexions – Val-d'Or";
    rcApi.get("/public").then((r) => setData(r.data)).catch(() => toast.error("Page indisponible pour le moment"));
  }, []);
  const s = data?.settings;
  return (
    <div className="min-h-screen font-manrope" style={{ background: RC.bg, color: RC.cream }} data-testid="rc-public-page">
      <header className="max-w-3xl mx-auto px-5 pt-10 sm:pt-16 pb-8">
        <div className="text-[11px] uppercase tracking-[0.35em] mb-4" style={{ color: RC.gold }}>Groupe social de danse · Val-d'Or</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-cabinet leading-[1.05]" data-testid="rc-title">
          Rythmes <span style={{ color: RC.coral }}>&</span> Connexions
        </h1>
        <p className="mt-4 text-lg sm:text-xl" style={{ color: RC.turquoise }}>{s?.sous_titre || "Écouter, bouger, apprendre et danser ensemble."}</p>
        <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="rc-intro">{s?.intro}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a href="#acces"><RcButton data-testid="rc-cta-acces" className="w-full sm:w-auto"><EnvelopeSimple size={20} /> Recevoir mon accès</RcButton></a>
          <Link to="/rythmes/mon-parcours"><RcButton variant="ghost" data-testid="rc-cta-login" className="w-full sm:w-auto">J'ai déjà un accès</RcButton></Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 space-y-12">
        <section>
          <RcTitle kicker="Ma prochaine rencontre">On se voit bientôt</RcTitle>
          <RcCard data-testid="rc-next-meeting">
            <div className="grid sm:grid-cols-3 gap-5">
              <Info icon={CalendarBlank} label="Quand" value={<span className="capitalize">{meetingLabel(data?.prochaine, s)}</span>} />
              <Info icon={MapPin} label="Où" value={data?.prochaine?.lieu || s?.lieu || "À confirmer"} />
              <Info icon={Backpack} label="Quoi apporter" value={s?.apporter || "—"} />
            </div>
          </RcCard>
        </section>

        <section>
          <RcTitle kicker="Comment ça se passe">Six petites étapes, à notre rythme</RcTitle>
          <div className="grid sm:grid-cols-2 gap-3">
            {(s?.progression || []).map((p, i) => {
              const Icon = STEP_ICONS[i] || Confetti;
              return (
                <div key={p} className="flex items-center gap-3 rounded-2xl border px-4 py-3.5" style={{ background: RC.card, borderColor: RC.line }}>
                  <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: RC.turquoise + "22" }}><Icon size={22} color={RC.turquoise} /></span>
                  <span className="text-base"><span style={{ color: RC.gold }} className="mr-2 font-bold">{i + 1}</span>{p}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <RcTitle kicker="Le parcours">Les rencontres</RcTitle>
          <div className="space-y-2.5">
            {(data?.sessions || []).map((m) => (
              <div key={m.numero} className="flex gap-4 rounded-2xl border px-4 py-3.5" style={{ background: RC.card, borderColor: RC.line, opacity: m.publie ? 1 : 0.55 }} data-testid={`rc-module-${m.numero}`}>
                <span className="text-2xl font-bold font-cabinet w-8 shrink-0" style={{ color: RC.coral }}>{m.numero}</span>
                <div><div className="font-bold">{m.theme}</div><div className="text-sm" style={{ color: RC.muted }}>{m.publie ? m.objectif : "À venir"}</div></div>
              </div>
            ))}
          </div>
        </section>

        <RulesBox regles={s?.regles || []} />

        <section id="acces" className="scroll-mt-20">
          <RcTitle kicker="Mon accès">Recevoir mon lien par courriel</RcTitle>
          <AccessForm />
        </section>
      </main>
      <RcFooter />
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      <Icon size={22} color={RC.gold} className="shrink-0 mt-0.5" />
      <div><div className="text-[11px] uppercase tracking-widest" style={{ color: RC.muted }}>{label}</div><div className="text-base leading-snug">{value}</div></div>
    </div>
  );
}

function AccessForm() {
  const nav = useNavigate();
  const [f, setF] = useState({ prenom: "", email: "", preferences: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await rcApi.post("/inscription", { ...f, origin: window.location.origin });
      setDone(r.data);
      if (!r.data.ok) toast.error(r.data.message);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) {
    return (
      <RcCard data-testid="rc-access-done">
        <p className="text-lg font-bold">{done.ok ? "C'est envoyé !" : "Oups"}</p>
        <p className="mt-1" style={{ color: RC.muted }}>{done.message}</p>
        {done.debug_code && (
          <div className="mt-4 rounded-xl p-4 text-center" style={{ background: RC.bg }} data-testid="rc-debug-code">
            <div className="text-xs uppercase tracking-widest" style={{ color: RC.muted }}>Code (préversion)</div>
            <div className="text-3xl font-mono tracking-[0.4em] mt-1">{done.debug_code}</div>
          </div>
        )}
        <RcButton variant="gold" className="mt-5" data-testid="rc-goto-parcours" onClick={() => nav(`/rythmes/mon-parcours?email=${encodeURIComponent(f.email)}`)}>Entrer mon code</RcButton>
      </RcCard>
    );
  }
  return (
    <RcCard>
      <form onSubmit={submit} className="grid gap-4" data-testid="rc-access-form">
        <Field label="Prénom" value={f.prenom} onChange={(v) => setF({ ...f, prenom: v })} required testid="rc-input-prenom" />
        <Field label="Courriel" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} required testid="rc-input-email" />
        <Field label="Préférences (facultatif) — danses qui t'attirent, pratiquer seul ou à deux…" value={f.preferences} onChange={(v) => setF({ ...f, preferences: v })} testid="rc-input-preferences" />
        <p className="text-xs" style={{ color: RC.muted }}>Nous demandons seulement ton prénom et ton courriel. Ton carnet restera visible par toi et l'encadrant uniquement.</p>
        <RcButton type="submit" disabled={busy} data-testid="rc-access-submit">{busy ? "Envoi…" : "Recevoir mon accès"}</RcButton>
      </form>
    </RcCard>
  );
}

export function Field({ label, value, onChange, type = "text", required, testid, textarea }) {
  const cls = "w-full rounded-xl border px-4 py-3 text-base outline-none focus:ring-2";
  const style = { background: RC.bg, borderColor: RC.line, color: RC.cream, "--tw-ring-color": RC.turquoise };
  return (
    <label className="block">
      <span className="block text-sm mb-1.5" style={{ color: RC.muted }}>{label}</span>
      {textarea
        ? <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className={cls} style={style} />
        : <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className={cls} style={style} />}
    </label>
  );
}
